import type { GradeScaleFormSchema } from '@repo/schemas/grade-scale/gradeScale';

import { useFormContext } from 'react-hook-form';

import { FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { NumberInputHorizontal } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

import type { GradeBand } from './gradeScale.types';

import { getBandSpan } from './gradeScaleBands';
import { gradeScaleColorAt, gradeScalePalette } from './gradeScaleColors';

export interface GradeScaleTableProps {
  schemeIndex: number;
  bands: GradeBand[];
  onChangeMin: (index: number, value: number | undefined) => void;
  onChangeMax: (index: number, value: number | undefined) => void;
}

export function GradeScaleTable(gradeScaleTableProps: GradeScaleTableProps) {
  const { schemeIndex, bands, onChangeMin, onChangeMax } = gradeScaleTableProps;
  const form = useFormContext<GradeScaleFormSchema>();

  const palette: string[] = gradeScalePalette(bands.length);

  return (
    <div className="border-border-primary overflow-x-auto rounded-lg border">
      <Table className="min-w-[41rem]">
        <TableHeader>
          <TableRow>
            <TableHead className="text-foreground min-w-32">Nazwa</TableHead>
            <TableHead className="text-foreground w-48">Min %</TableHead>
            <TableHead className="text-foreground w-48">Max %</TableHead>
            <TableHead className="text-foreground w-24">Rozpiętość</TableHead>
            <TableHead className="text-foreground w-12">Kolor</TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {bands.map((band, index) => {
            const isFirst: boolean = index === 0;
            const isLast: boolean = index === bands.length - 1;
            const span: number = getBandSpan(bands, index);

            return (
              // Rows are fully controlled by form state, so the index is a
              // stable enough key and never desyncs from the watched bands.
              <TableRow key={index}>
                <TableCell>
                  <FormField
                    control={form.control}
                    name={`schemes.${schemeIndex}.bands.${index}.name`}
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input {...field} readOnly placeholder="Nazwa oceny" aria-label="Nazwa oceny" />
                        </FormControl>
                        {/* Collapse reserved message space so the input stays
                            vertically centered with the other table cells. */}
                        <FormMessage className="empty:hidden" />
                      </FormItem>
                    )}
                  />
                </TableCell>

                <TableCell>
                  {/* The continuity errors from the schema land on the percent
                      paths, so each cell needs its own FormMessage surface. */}
                  <FormField
                    control={form.control}
                    name={`schemes.${schemeIndex}.bands.${index}.minPercent`}
                    render={() => (
                      <FormItem>
                        {isFirst ? (
                          <Input value={band.minPercent} readOnly className="text-center" aria-label="Min %" />
                        ) : (
                          <NumberInputHorizontal
                            value={band.minPercent}
                            min={(bands[index - 1]?.minPercent ?? 0) + 1}
                            max={band.maxPercent}
                            onChange={(value) => onChangeMin(index, value)}
                            aria-label="Min %"
                          />
                        )}
                        <FormMessage className="empty:hidden" />
                      </FormItem>
                    )}
                  />
                </TableCell>

                <TableCell>
                  <FormField
                    control={form.control}
                    name={`schemes.${schemeIndex}.bands.${index}.maxPercent`}
                    render={() => (
                      <FormItem>
                        {isLast ? (
                          <Input value={band.maxPercent} readOnly className="text-center" aria-label="Max %" />
                        ) : (
                          <NumberInputHorizontal
                            value={band.maxPercent}
                            min={band.minPercent}
                            max={(bands[index + 1]?.maxPercent ?? 100) - 1}
                            onChange={(value) => onChangeMax(index, value)}
                            aria-label="Max %"
                          />
                        )}
                        <FormMessage className="empty:hidden" />
                      </FormItem>
                    )}
                  />
                </TableCell>

                <TableCell className="text-muted-foreground">{span}%</TableCell>

                <TableCell>
                  <span className="border-border-primary grid size-9 place-items-center rounded-md border">
                    <span className="size-6 rounded-sm" style={{ backgroundColor: palette[index] ?? gradeScaleColorAt(0) }} aria-hidden />
                  </span>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
